import {Injectable} from '@angular/core';
import {GraphQLError} from 'graphql/index';
import {InMemoryCache} from '@apollo/client/core';
import {environment} from '../../../environments/environment';
import {IpService} from './ip.service';
import {Apollo} from 'apollo-angular';
import {catchError, firstValueFrom, map} from 'rxjs';
import {HttpLink} from 'apollo-angular/http';
import {ToastService} from './toast.service';
import {Router} from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class GraphqlService {
  constructor(
    private apollo: Apollo,
    private httpLink: HttpLink,
    private ipService: IpService,
    private messageService: ToastService,
    private router: Router,
  ) {
  }

  createLocalClient(localIPAddress: string) {
    this.apollo.removeClient('LOCAL_SERVER');

    const http = this.httpLink.create({
      uri: environment.localGraphqlUri.replace('{ip}', localIPAddress),
    });

    this.apollo.createNamed('LOCAL_SERVER', {
      link: http,
      cache: new InMemoryCache({
        addTypename: false,
      }),
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
        'Access-Control-Allow-Private-Network': 'true',
        'Access-Control-Request-Private-Network': 'true',
      },
      defaultOptions: {
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        mutate: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      },
    });
  }

  async runQuery(
    query: any,
    variables?: any,
    serverType?: string,
  ): Promise<any | GraphQLError> {
    serverType = this.ipService.getServerType(serverType);
    return await firstValueFrom(
      this.apollo
        .use(serverType)
        .query({
          query,
          variables,
          errorPolicy: 'all',
        })
        .pipe(
          map((res) => {
            if (res.errors) {
              if (res.errors[0].message === 'Signature has expired') {
                this.router.navigate(['/login']);
              }
              if (res.errors.length > 0 && res.errors[0].extensions) {
                const message: unknown = res.errors[0].extensions['message'];
                this.messageService.show({
                  message: message as string,
                  delay: 5000,
                  header: 'Error',
                });
                throw new GraphQLError(res.errors[0].message, {
                  extensions: res.errors[0].extensions,
                });
              }
            }
            return res.data;
          }),
          catchError((err) => {
            throw new GraphQLError(err.message, {
              extensions: err.extensions,
            });
          }),
        ),
    );
  }

  async runMutation(
    mutation: any,
    variables?: any,
    serverType?: string,
  ): Promise<any | GraphQLError> {
    serverType = this.ipService.getServerType(serverType);

    return await firstValueFrom(
      this.apollo
        .use(serverType)
        .mutate({
          mutation: mutation,
          variables,
          errorPolicy: 'all',
        })
        .pipe(
          map((res) => {
            if (
              res.errors &&
              res.errors.length > 0 &&
              res.errors[0].extensions
            ) {
              const message: unknown = res.errors[0]['message'];
              this.messageService.show({
                message: (message as string).substring(0, 255),
                delay: 5000,
                header: 'Error',
              });
              throw new GraphQLError(res.errors[0].message, {
                extensions: res.errors[0].extensions,
              });
            }
            return res.data;
          }),
        ),
    );
  }
}
